import {
  Home, CalendarClock, CalendarDays, CalendarRange, Sparkles, ShieldCheck,
  LogIn, LogOut, FileCheck2, Building2, BedDouble, CookingPot, Bath, Flame,
  PanelsTopLeft, Wind, Refrigerator, PaintRoller, Blinds,
} from "lucide-react";
import { IMAGES } from "./business";

// 19 residential cleaning services. Each has its own page at /services/:slug
export const SERVICES = [
  {
    slug: "regular-house-cleaning",
    title: "Regular House Cleaning",
    icon: Home,
    image: IMAGES.livingSage,
    short: "Consistent, thorough cleaning to keep your home fresh week after week.",
    long: "Our regular house cleaning keeps every room looking its best on a schedule that suits you. Perfect for busy households that want a reliably clean home without lifting a finger.",
    includes: ["Dusting all surfaces", "Vacuuming & mopping floors", "Kitchen & bathroom wipe-down", "Emptying bins", "Tidying living areas"],
    keywords: "regular house cleaning Melbourne",
  },
  {
    slug: "weekly-cleaning",
    title: "Weekly Cleaning",
    icon: CalendarClock,
    image: IMAGES.livingBright,
    short: "A spotless home, every single week, on a day that works for you.",
    long: "Weekly cleaning is ideal for families and busy professionals who value a consistently fresh home. We build a routine around your household so nothing gets missed.",
    includes: ["Full home refresh weekly", "Kitchen & bathrooms", "Floors throughout", "Dusting & surfaces", "Bins & tidy-up"],
    keywords: "weekly cleaning Melbourne",
  },
  {
    slug: "fortnightly-cleaning",
    title: "Fortnightly Cleaning",
    icon: CalendarDays,
    image: IMAGES.bedroomSunlit,
    short: "Every two weeks — a balanced, cost-effective way to stay on top of things.",
    long: "Fortnightly cleaning strikes the perfect balance between cost and cleanliness, keeping your home comfortable and presentable throughout the month.",
    includes: ["Whole-home clean every 2 weeks", "Bathrooms & kitchen", "Vacuum & mop", "Dusting", "General tidy"],
    keywords: "fortnightly cleaning Melbourne",
  },
  {
    slug: "monthly-cleaning",
    title: "Monthly Cleaning",
    icon: CalendarRange,
    image: IMAGES.livingOpen,
    short: "A monthly deep refresh to reset your home from top to bottom.",
    long: "Our monthly service gives your home a comprehensive reset, focusing on the areas that build up over time so everything feels renewed.",
    includes: ["Detailed monthly clean", "Kitchen & bathrooms", "Floors & skirting", "Dusting high & low", "Bins & tidy"],
    keywords: "monthly cleaning Melbourne",
  },
  {
    slug: "one-off-cleaning",
    title: "One-Off Cleaning",
    icon: Sparkles,
    image: IMAGES.livingSage,
    short: "A single, detailed clean whenever you need it — no commitment.",
    long: "Need a clean before guests arrive or after a busy stretch? Our one-off cleaning delivers a thorough result with no ongoing commitment.",
    includes: ["Full one-time clean", "Kitchen & bathrooms", "All floors", "Dusting throughout", "Tidy & bins"],
    keywords: "one off cleaning Melbourne",
  },
  {
    slug: "deep-cleaning",
    title: "Deep Cleaning",
    icon: ShieldCheck,
    image: IMAGES.cleaner,
    short: "An intensive, top-to-bottom clean reaching every hidden corner.",
    long: "Our deep cleaning goes far beyond the surface — tackling built-up grime, detailed areas and the spots regular cleaning misses for a truly restored home.",
    includes: ["Detailed room-by-room clean", "Skirting, doors & frames", "Kitchen degrease", "Bathroom descale", "Behind & under furniture (accessible)"],
    keywords: "deep cleaning Melbourne",
  },
  {
    slug: "move-in-cleaning",
    title: "Move-In Cleaning",
    icon: LogIn,
    image: IMAGES.kitchenIsland,
    short: "Start fresh in a spotless new home before you unpack a single box.",
    long: "Move into a home that feels brand new. We clean every room thoroughly so your fresh start is truly fresh.",
    includes: ["Full property clean", "Kitchen inside & out", "Bathrooms sanitised", "Floors throughout", "Cupboards wiped"],
    keywords: "move in cleaning Melbourne",
  },
  {
    slug: "move-out-cleaning",
    title: "Move-Out Cleaning",
    icon: LogOut,
    image: IMAGES.livingBright,
    short: "Leave your old home immaculate and hand back the keys with confidence.",
    long: "Moving out is stressful enough. We handle the clean so you can focus on your move and leave the property in excellent condition.",
    includes: ["Whole-home clean-out", "Kitchen & appliances", "Bathrooms detailed", "Floors & skirting", "Marks & dust removed"],
    keywords: "move out cleaning Melbourne",
  },
  {
    slug: "end-of-lease-cleaning",
    title: "End-of-Lease Cleaning",
    icon: FileCheck2,
    image: IMAGES.kitchenWood,
    short: "A detailed end-of-lease clean to help you leave on great terms.",
    long: "Our thorough end-of-lease cleaning targets the detail that matters when you're handing back a rental, helping you leave the property spotless.",
    includes: ["Comprehensive detail clean", "Oven & kitchen", "Bathrooms descaled", "Skirting & frames", "Floors throughout"],
    keywords: "end of lease cleaning Melbourne",
  },
  {
    slug: "apartment-cleaning",
    title: "Apartment Cleaning",
    icon: Building2,
    image: IMAGES.livingOpen,
    short: "Cleaning tailored to apartments and units of every size.",
    long: "From studios to multi-bedroom apartments, we deliver a spotless result suited to compact living and shared building access.",
    includes: ["Full apartment clean", "Kitchen & bathroom", "Floors & surfaces", "Dusting throughout", "Bins & tidy"],
    keywords: "apartment cleaning Melbourne",
  },
  {
    slug: "airbnb-cleaning",
    title: "Airbnb Cleaning",
    icon: BedDouble,
    image: IMAGES.bedroomMirror,
    short: "Guest-ready turnovers that keep your reviews shining.",
    long: "Reliable, presentation-focused cleaning between guests so your property is always ready for a five-star stay.",
    includes: ["Turnover clean", "Fresh presentation", "Kitchen & bathroom", "Floors & surfaces", "Restock check (supplied)"],
    keywords: "airbnb cleaning Melbourne",
  },
  {
    slug: "kitchen-cleaning",
    title: "Kitchen Cleaning",
    icon: CookingPot,
    image: IMAGES.kitchenGold,
    short: "Sparkling benches, splashbacks, sinks and cupboard fronts.",
    long: "A focused kitchen clean that cuts through grease and grime, leaving benches, splashbacks, sinks and cabinetry gleaming.",
    includes: ["Benchtops & splashback", "Sink & tapware", "Cupboard fronts", "Stovetop", "Floor"],
    keywords: "kitchen cleaning Melbourne",
  },
  {
    slug: "bathroom-cleaning",
    title: "Bathroom Cleaning",
    icon: Bath,
    image: IMAGES.bathroomModern,
    short: "Fresh, sanitised and descaled bathrooms you'll love using.",
    long: "We descale, sanitise and polish every fixture so your bathrooms feel hygienic, fresh and spotless.",
    includes: ["Shower & screen descale", "Toilet sanitised", "Sink & tapware", "Mirrors polished", "Tiles & floor"],
    keywords: "bathroom cleaning Melbourne",
  },
  {
    slug: "oven-cleaning",
    title: "Oven Cleaning",
    icon: Flame,
    image: IMAGES.kitchenWood,
    short: "Baked-on grime removed for a like-new oven interior.",
    long: "A detailed oven clean that lifts baked-on grease and carbon, restoring racks, trays and glass to a fresh finish.",
    includes: ["Oven interior degrease", "Racks & trays", "Door glass", "Seals & edges", "Exterior wipe"],
    keywords: "oven cleaning Melbourne",
  },
  {
    slug: "interior-window-cleaning",
    title: "Interior Window Cleaning",
    icon: PanelsTopLeft,
    image: IMAGES.livingBright,
    short: "Streak-free interior glass to let the light back in.",
    long: "Crystal-clear interior windows, sills and tracks so more natural light fills your home.",
    includes: ["Interior glass", "Sills wiped", "Tracks cleaned", "Frames dusted", "Streak-free finish"],
    keywords: "interior window cleaning Melbourne",
  },
  {
    slug: "exterior-window-cleaning",
    title: "Exterior Window Cleaning",
    icon: Wind,
    image: IMAGES.melbourneStreet,
    short: "Clear exterior windows that lift your home's whole look.",
    long: "Safely accessible exterior window cleaning for a brighter, more welcoming home exterior.",
    includes: ["Accessible exterior glass", "Frames wiped", "Sills cleaned", "Streak-free finish", "Ground-level focus"],
    keywords: "exterior window cleaning Melbourne",
  },
  {
    slug: "fridge-cleaning",
    title: "Fridge Cleaning",
    icon: Refrigerator,
    image: IMAGES.kitchenIsland,
    short: "A hygienic, fresh-smelling fridge inside and out.",
    long: "We clean and sanitise your fridge interior, shelves and drawers for a fresh, hygienic result.",
    includes: ["Interior sanitise", "Shelves & drawers", "Seals wiped", "Exterior polish", "Fresh finish"],
    keywords: "fridge cleaning Melbourne",
  },
  {
    slug: "wall-cleaning",
    title: "Wall Cleaning",
    icon: PaintRoller,
    image: IMAGES.livingSage,
    short: "Marks, scuffs and dust gently removed from walls.",
    long: "Spot cleaning and dusting of walls to remove marks, scuffs and build-up for a refreshed interior.",
    includes: ["Mark & scuff removal", "Dusting walls", "Light switches", "Door frames", "Gentle products"],
    keywords: "wall cleaning Melbourne",
  },
  {
    slug: "blind-cleaning",
    title: "Blind Cleaning",
    icon: Blinds,
    image: IMAGES.bedroomSunlit,
    short: "Dust-free blinds for cleaner air and a tidier look.",
    long: "Careful dusting and wiping of blinds to remove built-up dust and keep your windows looking neat.",
    includes: ["Blind dusting", "Slat wipe-down", "Cords & fittings", "Sills cleaned", "Careful handling"],
    keywords: "blind cleaning Melbourne",
  },
];

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);

// Services offered as multi-select in the quote wizard (per spec)
export const QUOTE_SERVICES = [
  "Regular Cleaning", "Weekly", "Fortnightly", "Monthly", "One-Off",
  "Deep Cleaning", "End Of Lease", "Move In", "Move Out", "Kitchen",
  "Bathroom", "Oven", "Fridge", "Windows", "Walls", "Blinds", "Other",
];
